import {
  isHostTargetingStrategy,
  isKubernetesTargetingStrategy
} from 'components/attacks/create/targets/target-utils'
import { getTargetMultiValueMap } from 'components/attacks/utils/targets-multi-value-map'
import { cleanJoin } from 'utils/strings'

import type {
  AttackSamplingConfiguration,
  ContainerTargetingStrategy,
  EvenSampling,
  ExactSampling,
  HostTargetingStrategy,
  KubernetesTargetingStrategy,
  TargetingStrategy
} from 'types/api/attacks/details/targeting'

type TargetsHandlerArgs = {
  isTargetAll: boolean
  samplingLabel: string
  serviceName: string | undefined
}

export function isEvenSampling(
  sampling?: AttackSamplingConfiguration
): sampling is EvenSampling {
  return sampling?.type === 'Even'
}

export function isExactSampling(
  sampling?: AttackSamplingConfiguration
): sampling is ExactSampling {
  return sampling?.type === 'Exact'
}

export function getSamplingLabel(sampling?: AttackSamplingConfiguration) {
  if (!sampling) {
    return ''
  }

  if (isEvenSampling(sampling)) {
    const percent = sampling.percent
    return percent === 100 ? '' : `${percent}% of`
  }

  const count = sampling.count
  return count ? `${count} of` : ''
}

export function getK8sLabelSelectionDisplay({
  labelSelection
}: KubernetesTargetingStrategy) {
  if (!labelSelection) return ''

  const { allNamespaces, labelSelector, namespaces } = labelSelection

  if (allNamespaces) {
    return labelSelector
  }

  const hasNamespaces = namespaces && namespaces.length > 0
  const namespacesLabel = hasNamespaces && `(${namespaces.join(', ')})`

  return cleanJoin([labelSelector, namespacesLabel])
}

function handleKubernetesTargets(
  targetingStrategy: KubernetesTargetingStrategy,
  { isTargetAll, samplingLabel, serviceName }: TargetsHandlerArgs
): string[] {
  const baseDisplay = serviceName ? [serviceName] : []
  const serviceDisplay = serviceName ? `${serviceName} -` : undefined

  if (isTargetAll) {
    return [
      cleanJoin([
        serviceDisplay,
        'Targeting',
        samplingLabel,
        'all Kubernetes resources'
      ])
    ]
  }

  if (targetingStrategy.labelSelection) {
    return [
      cleanJoin([
        serviceDisplay,
        getK8sLabelSelectionDisplay(targetingStrategy)
      ])
    ]
  }

  if (targetingStrategy.names) {
    return [
      ...baseDisplay,
      ...targetingStrategy.names.map(
        ({ namespace, name }) => `${name} (${namespace})`
      ),
      ...(samplingLabel
        ? [`${samplingLabel} selected Kubernetes resources`]
        : [])
    ]
  }

  return []
}

function handleHostAndContainerTargets(
  targetingStrategy: ContainerTargetingStrategy | HostTargetingStrategy,
  { isTargetAll, samplingLabel, serviceName }: TargetsHandlerArgs
): string[] {
  const strategyLabel = isHostTargetingStrategy(targetingStrategy)
    ? 'Hosts'
    : 'Containers'

  const baseDisplay = serviceName ? [serviceName] : []
  const serviceDisplay = serviceName ? `${serviceName} -` : undefined

  if (isTargetAll) {
    return [
      cleanJoin([
        serviceDisplay,
        'Targeting',
        samplingLabel,
        `all ${strategyLabel}`
      ])
    ]
  }

  const valueMap = getTargetMultiValueMap(targetingStrategy)

  if (!valueMap) return baseDisplay

  return [
    ...baseDisplay,
    ...Object.entries(valueMap).map(
      ([key, values]) => `${key}: ${values.join(', ')}`
    ),
    ...(samplingLabel ? [`${samplingLabel} selected ${strategyLabel}`] : [])
  ]
}

/**
 * Returns the attack targets in a human-readable format
 */
export function getAttackTargetsForDisplay(
  targetingStrategy: TargetingStrategy,
  attackSamplingConfiguration?: AttackSamplingConfiguration,
  serviceName?: string | undefined
): string[] {
  if (targetingStrategy.ids?.length) {
    return [...(serviceName ? [serviceName] : []), ...targetingStrategy.ids]
  }

  const isKubernetes = isKubernetesTargetingStrategy(targetingStrategy)
  const commonArgs = {
    isTargetAll: Boolean(targetingStrategy.isAll),
    samplingLabel: getSamplingLabel(attackSamplingConfiguration),
    serviceName
  }

  return isKubernetes
    ? handleKubernetesTargets(targetingStrategy, commonArgs)
    : handleHostAndContainerTargets(targetingStrategy, commonArgs)
}
